/**
 * Scrolling that stays where it is pointed.
 *
 * `Element.scrollIntoView()` scrolls every scrollable ancestor it can find, and
 * inside an iframe that includes the page around the frame: every answer used
 * to jolt the whitepaper page the diagnostic is embedded in. Everything in the
 * feature scrolls through `scrollWithin` instead, which moves exactly one
 * container. The parent page is only ever moved by `public/embed.js`, when the
 * frame asks it to with `hw:scroll-to`.
 */

/** A scroll container: an element with its own overflow, or the window. */
export type ScrollContainer = HTMLElement | Window;

/**
 * `start` puts the target at the top of the container. `nearest` only scrolls
 * when the target is not fully visible, and then by as little as it can.
 */
export type ScrollAlign = "start" | "nearest";

export interface ScrollOptions {
  align?: ScrollAlign;
  /** Room kept between the target and the container edge, in CSS pixels. */
  margin?: number;
}

/** Where things are, in viewport pixels, at the moment of scrolling. */
export interface ScrollGeometry {
  /** The target's top edge. */
  targetTop: number;
  targetHeight: number;
  /** The top edge of the container's scrolling area (0 for the window). */
  viewTop: number;
  /** The visible height of the container. */
  viewHeight: number;
  /** How far the container is scrolled already. */
  scrollTop: number;
}

/**
 * The `scrollTop` that brings the target into view, or null when it already is
 * (`nearest` only). Pure, so the maths is testable without a layout engine.
 */
export function scrollTopFor(
  geometry: ScrollGeometry,
  { align = "start", margin = 0 }: ScrollOptions = {},
): number | null {
  const { targetTop, targetHeight, viewTop, viewHeight, scrollTop } = geometry;
  // The target's position inside the container's content, which is what
  // `scrollTop` counts in.
  const top = targetTop - viewTop + scrollTop;
  const start = Math.max(0, Math.round(top - margin));
  if (align === "start") {
    return start;
  }
  if (top - margin < scrollTop) {
    return start;
  }
  const bottom = top + targetHeight + margin;
  if (bottom <= scrollTop + viewHeight) {
    return null;
  }
  // Taller than the view: its top is the part worth seeing.
  if (targetHeight + margin * 2 > viewHeight) {
    return start;
  }
  return Math.max(0, Math.round(bottom - viewHeight));
}

/** A window is its own `window`; `instanceof Window` fails across realms. */
function isWindow(container: ScrollContainer): container is Window {
  return (container as Window).window === container;
}

function geometryOf(container: ScrollContainer, target: Element): ScrollGeometry {
  const rect = target.getBoundingClientRect();
  if (isWindow(container)) {
    return {
      targetTop: rect.top,
      targetHeight: rect.height,
      viewTop: 0,
      viewHeight: container.innerHeight,
      scrollTop: container.scrollY || 0,
    };
  }
  // The scrolling area starts inside the container's border.
  const box = container.getBoundingClientRect();
  return {
    targetTop: rect.top,
    targetHeight: rect.height,
    viewTop: box.top + container.clientTop,
    viewHeight: container.clientHeight,
    scrollTop: container.scrollTop,
  };
}

/** Scroll one container, and only that one, to a top offset. */
export function scrollContainerTo(
  container: ScrollContainer,
  top: number,
  behavior: ScrollBehavior,
): void {
  if (typeof container.scrollTo === "function") {
    container.scrollTo({ top, behavior });
    return;
  }
  // Without `scrollTo` (older engines, jsdom) the jump is still worth making.
  if (!isWindow(container)) {
    container.scrollTop = top;
  }
}

/** Bring `target` into view inside `container`, touching no other scroller. */
export function scrollWithin(
  container: ScrollContainer,
  target: Element,
  behavior: ScrollBehavior,
  options: ScrollOptions = {},
): void {
  const top = scrollTopFor(geometryOf(container, target), options);
  if (top !== null) {
    scrollContainerTo(container, top, behavior);
  }
}

/**
 * The container that actually scrolls around `element`.
 *
 * Embedded, the region is a fixed-height box by construction and is always the
 * answer: falling back to the window there would mean nothing (the frame does
 * not scroll) at best. On its own page the same region only scrolls from
 * 900px up; below that the page does, so the window is returned. Null when
 * there is nothing to scroll yet.
 */
export function resolveScrollContainer(
  element: HTMLElement | null,
  embedded: boolean,
): ScrollContainer | null {
  if (!element) {
    return embedded ? null : typeof window === "undefined" ? null : window;
  }
  if (embedded) {
    return element;
  }
  const overflow = window.getComputedStyle(element).overflowY;
  return overflow === "auto" || overflow === "scroll" ? element : window;
}
